import {
  Box,
  Button,
  Switch,
  useTheme,
  TextField,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
  CircularProgress,
  FormControlLabel,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useLookup } from 'hooks';
import { Role } from 'library/types';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { renderAutoCompleteLocaleText } from 'utils';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

export type UpdateActivityTypeFormParams = {
  roleIds: number[];
  title: string;
  isEffortActivity: boolean;
  isPermissionActivity: boolean;
};

const defaultValues: UpdateActivityTypeFormParams = {
  roleIds: [],
  title: '',
  isEffortActivity: false,
  isPermissionActivity: false,
};

type UpdateActivityTypeDialogProps = {
  open: boolean;
  activityTypeId: GridRowId;
  handleClose: () => void;
  refresh: () => Promise<void>;
  setRowSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
};

const UpdateActivityTypeDialog = ({
  open,
  activityTypeId,
  handleClose: closeDialog,
  refresh,
  setRowSelectionModel,
}: UpdateActivityTypeDialogProps) => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const {
    items: roles,
    loading: loadingRoles,
    loaded: rolesLoaded,
  } = useLookup(apiService.getRoles, { enabled: open });

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);

  // form validations & form state
  const schema = yup.object().shape({
    title: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    roleIds: yup
      .array()
      .of(
        yup
          .number()
          .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      )
      .min(
        1,
        intl.formatMessage({
          id: 'validations.activityTypes.role.minOne',
          defaultMessage: 'En az 1 rol seçilmelidir.',
        })
      )
      .required(),
  });

  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateActivityTypeFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const onSubmit = async (data: UpdateActivityTypeFormParams) => {
    const postData = {
      ...data,
      id: activityTypeId as number,
    };

    try {
      setLoading(true);

      const response = await apiService.updateActivityType(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="activityTypes.updateActivityTypeCompleted"
            defaultMessage="Aktivite Türü düzenleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
        setRowSelectionModel([]);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getActivityTypeInfos = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getSpecificActivityType(activityTypeId as number);

      if (response.isSuccess) {
        setValue('roleIds', response.data.roleIds);
        setValue('title', response.data.title);
        setValue('isEffortActivity', response.data.isEffortActivity);
        setValue('isPermissionActivity', response.data.isPermissionActivity);

        const selected = roles.filter((role) => response.data.roleIds.includes(role.id));
        setSelectedRoles(selected);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [activityTypeId, roles, setValue]);

  // sync helper functions
  const handleClose = () => {
    closeDialog();
    reset();
    setSelectedRoles([]);
  };

  // useEffect hooks
  useEffect(() => {
    if (open && activityTypeId !== undefined && activityTypeId !== null && rolesLoaded) {
      getActivityTypeInfos();
    }
  }, [open, activityTypeId, rolesLoaded, getActivityTypeInfos]);

  return (
    <ResponsiveDialog maxWidth="md" open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage id="activityTypes.updateActivityType" defaultMessage="Aktivite Türü Düzenle" />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <ResponsiveDialogContent>
          <DialogContentText>
            <FormattedMessage
              id="activityTypes.updateActivityTypeText"
              defaultMessage="İlgili alanları değiştirip devam ederek seçili aktivite türü için düzenleme yapabilirsiniz."
            />
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12 }}>
              <Autocomplete
                size="small"
                multiple
                filterSelectedOptions
                disableCloseOnSelect
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                options={roles}
                loading={loadingRoles}
                value={selectedRoles}
                {...renderAutoCompleteLocaleText(intl)}
                onChange={(_, newValue: Role[]) => {
                  setSelectedRoles(newValue);

                  setValue(
                    'roleIds',
                    newValue.map((v) => v.id)
                  );
                }}
                getOptionLabel={(option: Role) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        width: 'fit-content',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                      '& .MuiInputBase-root': {
                        height: 'auto',
                      },
                    }}
                    label={<FormattedMessage id="activityTypes.roles" defaultMessage="Roller" />}
                    error={!!errors.roleIds}
                    helperText={errors.roleIds?.message}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingRoles ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="title"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <TextField
                    {...rest}
                    label={<FormattedMessage id="activityTypes.title" defaultMessage="Adı" />}
                    fullWidth
                    size="small"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="isEffortActivity"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label={
                      <FormattedMessage id="activityTypes.isEffortActivity" defaultMessage="Efor Aktivitesi mi?" />
                    }
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }}>
              <Controller
                name="isPermissionActivity"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label={
                      <FormattedMessage id="activityTypes.isPermissionActivity" defaultMessage="İzin Aktivitesi mi?" />
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </ResponsiveDialogContent>
        <ResponsiveDialogActions>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </ResponsiveDialogActions>
      </Box>
      <LoadingBackdrop open={loading} />
    </ResponsiveDialog>
  );
};

export default UpdateActivityTypeDialog;
